import React, { useEffect, useState } from "react";
import { fetchPipelineRuns, triggerPipeline } from "@/lib/api";
import { toast } from "sonner";
import { PlayCircle } from "@phosphor-icons/react";

export default function Pipeline() {
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const r = await fetchPipelineRuns();
      setRuns(r);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const onRun = async () => {
    setRunning(true);
    try {
      await triggerPipeline();
      toast.success("Pipeline-Run erfolgreich");
      await load();
    } catch {
      toast.error("Pipeline fehlgeschlagen");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pipeline-root">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eid-label">Cron · ETL · Telemetrie</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Pipeline Status</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Tägliche Cron-Ausführung 04:15 Europe/Berlin. Alle externen Calls
            laufen proxyseitig ohne Client-Metadaten.
          </p>
        </div>
        <button
          onClick={onRun}
          disabled={running}
          data-testid="run-now-btn"
          className="flex items-center gap-2 bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-60 transition-colors rounded-sm px-4 py-2 text-sm"
        >
          <PlayCircle size={16} /> {running ? "Läuft…" : "Jetzt ausführen"}
        </button>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="eid-card p-4 lg:col-span-2" data-testid="runs-terminal">
          <h3 className="text-lg font-medium mb-3">Letzte Runs</h3>
          <div className="eid-terminal h-[420px] overflow-y-auto">
            {runs.length === 0 && <div className="dim">$ warte auf erste Ausführung…</div>}
            {runs.map((r, idx) => (
              <div key={idx} className="mb-3">
                <div>
                  <span className="dim">[{r.started_at?.slice(0, 19)?.replace("T", " ")}]</span>{" "}
                  <span className={r.status === "ok" ? "ok" : r.status === "partial" ? "warn" : "err"}>
                    {r.status?.toUpperCase()}
                  </span>{" "}
                  · duration {r.duration_s}s
                </div>
                <div className="dim">
                  regions_ok = [{(r.regions_ok || []).join(", ")}]
                </div>
                {(r.errors || []).map((e, i) => (
                  <div key={i} className="err">× {e.region_id}: {e.error}</div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <aside className="eid-card p-4 space-y-3" data-testid="sources-panel">
          <h3 className="text-lg font-medium">Datenquellen</h3>
          <Source name="Copernicus CDSE" status="oauth-pending" note="Sentinel-2/3 · EU (ESA)" />
          <Source name="Open-Meteo" status="live" note="NWP · Schweiz" />
          <Source name="yfinance" status="live" note="EOD futures" />
          <Source name="Alpha Vantage" status="fallback" note="Backup EOD" />
          <Source name="Emergent LLM" status="live" note="Gemini 3 Flash" />
          <div className="eid-divider" />
          <div>
            <div className="eid-label">Privacy Proxy</div>
            <div className="text-sm mt-1">
              Ausgehende Calls → <span className="font-mono text-[#00FA9A]">User-Agent „EID-ETL/1.0"</span>
            </div>
            <div className="text-sm">Keine Client-IPs · keine Cookies · abstrakte bbox</div>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Source({ name, status, note }) {
  const color =
    status === "live" ? "bg-[#00FA9A]" : status === "fallback" ? "bg-[#D98324]" : "bg-[#8A8A8A]";
  return (
    <div className="flex items-center justify-between border border-white/10 rounded-sm px-3 py-2">
      <div>
        <div className="text-sm font-medium">{name}</div>
        <div className="eid-label">{note}</div>
      </div>
      <span className={`text-[10px] font-mono uppercase tracking-wider text-white ${color} px-2 py-1 rounded-sm`}>
        {status}
      </span>
    </div>
  );
}
